import React, { useState, useEffect} from 'react';
import '../view-archer-styles.css';

//material-ui
import { Button, Chip, Card, CardHeader, CardContent, CircularProgress, Grid, IconButton, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material/';
import { blue} from '@mui/material/colors';
import { Edit, DeleteOutline, Check} from '@mui/icons-material/';

//components import
import NewReferralDialog from './NewReferralDialog';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import LegalReferral from '../dataObjects/LegalReferral';
import flaggedItemGroup from '../dataObjects/FlaggedItemGroup';
import FlaggedItem from '../dataObjects/FlaggedItem';
import ArcherIssue from '../dataObjects/ArcherIssue';

interface ReferralProps {
    auditorId: number;
    displayStatus: string;
}


const ReferralManager = (props: ReferralProps) => {

    useEffect(() => {
        if (props.displayStatus === "active") {
            getPendingLegalReferrals();
        }
    }, [])

    /*DISPLAY PENDING REFERRALS*/
    const [pendingReferrals, setPendingReferrals] = useState<LegalReferral[]>();

    const getPendingLegalReferrals = async () => {
        let url = `/Archer3/GetPendingLegalReferrals?auditorId=${props.auditorId}`
        await fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetPendingLegalReferrals',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let data = result.result;
                    setLoadingIndicator('hidden');
                    setPendingReferrals(data);
                }
            }
            )
    }

    /*CREATE NEW REFERRAL*/
    const [isNewReferralDialogOpen, setNewReferralDialogOpen] = useState(false);

    const openNewReferralDialog = () => {
        setNewReferralDialogOpen(true);
    };

    const closeNewReferralDialog = () => {
        setNewReferralDialogOpen(false);
    };


    /*EDIT REFERRAL*/
    const [isEditingMode, setEditingMode] = useState(false);
    const [selectedReferral, setSelectedReferral] = useState<LegalReferral>();
    const [referralIssues, setReferralIssues] = useState<ArcherIssue[]>();

    const editReferral = (referralId: number) => {
        let referral = pendingReferrals?.find(referral => referral.id === referralId);
        setSelectedReferral(referral);
        setReferralIssues(referral?.issues);
        setEditingMode(true);
    }

    const cancelEditing = () => {
        setEditingMode(false);
    }

    const saveReferral = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries((formData as any).entries());
        const cpfId = formJson.cpfId;
        const id = formJson.id;
        const issuesDescription = formJson.issuesDescription;
        const relevantBackground = formJson.relevantBackground;
        const auditorRecommendation = formJson.auditorRecommendation;
        const phoneCallDate = formJson.phoneCallDate;

        var params = [
            `id=${id}`,
            `cpfId=${cpfId}`,
            `issuesDescription=${issuesDescription}`,
            `relevantBackground=${relevantBackground}`,
            `auditorRecommendation=${auditorRecommendation}`,
            `phoneCallDate=${phoneCallDate}`
        ].join('&');

        let url = `/Archer3/saveLegalReferral?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: `Error: Saving Referral...`,
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                  setEditingMode(false);
                  getPendingLegalReferrals();
                }
            })
    }

    const confirmDeleteReferral = (referralId: number) => {
        let info = {
            type: 'warning',
            title: 'Warning: Delete Pending Referral',
            buttonLabel: "Delete",
            errorMessage: 'Are you sure you would like to delete this pending referral?',
            secondaryErrorMessage: ''
        };

        let action = {
            type: 'deleteReferral',
            id: referralId
        }

        setConfirmationAction(action);
        setAlertInfo(info);
        setAlertInfoDialog(true);
    }

    const deleteReferral = (referralId: number) => {
        let url = `/Archer3/DeleteReferral?referralId=${referralId}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: DeleteReferral',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setConfirmationAction({ type: '', id: 0 });
                    setAlertInfoDialog(false);
                    getPendingLegalReferrals();
                    
                }
            })
    }

    const confirmFinalizeReferral = (referralId: number) => {
        let info = {
            type: 'info',
            title: 'Finalize Pending Referral...',
            buttonLabel: "Finalize",
            errorMessage: 'Are you sure you would like to finalize this pending referral?',
            secondaryErrorMessage: ''
        };

        let action = {
            type: 'finalizeReferral',
            id: referralId
        }

        setConfirmationAction(action);
        setAlertInfo(info);
        setAlertInfoDialog(true);
    }

    const finalizeReferral = (referralId: number) => {
        let url = `/Archer3/FinalizeReferral?referralId=${referralId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: FinalizeReferral',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setConfirmationAction({ type: '', id: 0 });

                    let successMessage = 'Referral was finalized successfully and is in 205 tracker to be approved!';
                    let info = {
                        type: 'success',
                        title: 'Referral Finalized',
                        errorMessage: successMessage,
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    getPendingLegalReferrals();
                }
            }
            )
    }

    const deleteIssueFromReferral = (referralId:number, issueId: number) => {
        let url = `/Archer3/removeIssueFromReferral?referralId=${referralId}&issueID=${issueId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: deleteIssueFromReferral',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let updatedIssues = selectedReferral?.issues.filter(issue => issue.issueId !== issueId);
                    setReferralIssues(updatedIssues);
                }
            })
    }

    //loading indicator
    const [isLoading, setLoadingIndicator] = useState('');

    /*Error Handling*/
    const [confirmActionData, setConfirmationAction] = useState({
        type: '',
        id: 0,
    })
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const confirmAction = (confirm: boolean) => {
        let type = confirmActionData.type;
        let id = confirmActionData.id;
        if (confirm) {
            switch (type) {
                case 'deleteReferral':
                    deleteReferral(id);
                    break;
                case 'finalizeReferral':
                    finalizeReferral(id);
                    break;
            }
        }
    }

    return (
        <div className="referral-manager">
            <Grid container>
                <Grid item xs={6} sx={{ textAlign: 'left' }}>
                    <Chip className="is-clickable" label='Refer Issues' variant="outlined" color="primary" onClick={openNewReferralDialog} />
                </Grid>
            </Grid>
            <Grid container>
                {
                    !isEditingMode && 
                    pendingReferrals?.map(
                        (referral) =>
                            <Grid item xs={10} key={referral.id} sx={{marginTop:3}}>
                                <Card variant="outlined" className="referral-card">
                                    <CardHeader
                                        className="card-header"
                                        title={<span className="referral-title">{referral.creationDate} {referral.cpfId} {referral.filerName}</span>}
                                        action={
                                            <Stack direction="row" spacing={0} alignItems="center">
                                                <IconButton title="Edit Referral" onClick={() => editReferral(referral.id)}>
                                                    <Edit color="primary" className="small-icon" />
                                                </IconButton>
                                                <IconButton title="Delete Referral" onClick={() => confirmDeleteReferral(referral.id)}>
                                                    <DeleteOutline color="error" className="small-icon" />
                                                </IconButton>
                                                <Chip sx={{marginRight:1}} label='Finalize' size="small" title="Finalize Referral" variant="outlined" color="success" onClick={() => confirmFinalizeReferral(referral.id)} />
                                            </Stack>
                                        }
                                    />
                                    <CardContent className="card-content">
                                        {/* SUMMARY */}
                                        <Grid container className="referral-section">
                                            <Typography variant="subtitle2">
                                                <u>SUMMARY</u>
                                            </Typography>
                                        </Grid>
                                        <Grid container className="referral-section">
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle2" sx={{ color: blue[800] }}>Issue Description</Typography>
                                            </Grid>
                                            <Grid item xs={9}>
                                                <Typography variant="body2">{referral.issuesDescription}</Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid container className="referral-section">
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle2" sx={{ color: blue[800] }}>Relevant Background</Typography>
                                            </Grid>
                                            <Grid item xs={9}>
                                                <Typography variant="body2">{referral.relevantBackground}</Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid container className="referral-section">
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle2" sx={{ color: blue[800] }}>Auditor Recommendation:</Typography>
                                            </Grid>
                                            <Grid item xs={9}>
                                                <Typography variant="body2">{referral.auditorRecommendation}</Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid container className="referral-section">
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle2" sx={{ color: blue[800] }}>Date Phone Call Made:</Typography>
                                            </Grid>
                                            <Grid item xs={9}>
                                                <Typography variant="body2">{referral.callDate}</Typography>
                                            </Grid>
                                        </Grid>
                                        {/*ISSUES*/}
                                        <Grid container className="referral-section">
                                            <Typography variant="subtitle2">
                                                <u>ISSUES</u>
                                            </Typography>
                                        </Grid>
                                        {
                                            referral.issues.map((issue) =>
                                                <Grid key={issue.issueId}>
                                                    <Grid container className="referral-section">
                                                        <Grid item xs={10}>
                                                            <Typography variant="subtitle2" sx={{ color: blue[800] }}>{issue.issueTypeCode} {issue.header}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container className="referral-section">
                                                        <Grid item xs={6}>
                                                            <TableContainer>
                                                                <Table stickyHeader sx={{ minWidth: 200 }} size="small" aria-label="Flagged Items Table">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell>Date</TableCell>
                                                                            <TableCell>Name</TableCell>
                                                                            <TableCell>Amount</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {
                                                                            issue.flaggedItems.map((flag, index) =>
                                                                                <TableRow key={flag.flaggedItemId} >
                                                                                    <TableCell>{flag.date}</TableCell>
                                                                                    <TableCell>{flag.name}</TableCell>
                                                                                    <TableCell align="right">{flag.amount}</TableCell>
                                                                                </TableRow>
                                                                            )
                                                                        }
                                                                        {
                                                                            issue.flaggedItemGroups.length > 0 &&
                                                                            issue.flaggedItemGroups.map((group: flaggedItemGroup) => {
                                                                                return (
                                                                                    <>
                                                                                        {
                                                                                            group.flaggedItems.length > 0 &&
                                                                                            group.flaggedItems.map((item: FlaggedItem, index: number) => (
                                                                                                <TableRow
                                                                                                    key={item.flaggedItemId}
                                                                                                    className={"flagged-" + item.flaggedItemId}
                                                                                                >
                                                                                                    <TableCell>
                                                                                                        {item.date}<br />
                                                                                                    </TableCell>
                                                                                                    <TableCell>
                                                                                                        {item.fullName}<br />
                                                                                                    </TableCell>
                                                                                                    <TableCell align="right">
                                                                                                        {item.amount}
                                                                                                    </TableCell>
                                                                                                </TableRow>

                                                                                            ))
                                                                                        }
                                                                                        {
                                                                                            <TableRow key={group.groupUi} >
                                                                                                <TableCell></TableCell>
                                                                                                <TableCell><i>Amount to Refund:</i></TableCell>
                                                                                                <TableCell>{group.amountToRefund}</TableCell>
                                                                                            </TableRow>
                                                                                        }
                                                                                    </>
                                                                                )
                                                                            })
                                                                        }
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )
                                        }
                                    </CardContent>
                                </Card>
                            </Grid>

                    )
                }
                {
                    isEditingMode &&
                    selectedReferral &&
                    <Grid item xs={10} sx={{ marginTop: 3 }}>
                            <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => saveReferral(event)}>
                                <Card variant="outlined" className="referral-card">
                                    <CardHeader
                                        className="card-header"
                                        title={<span className="referral-title">{selectedReferral.creationDate} {selectedReferral.cpfId} </span>}
                                        action={
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Button variant="outlined" color="warning" size="small" onClick={()=>cancelEditing()}>
                                                    Cancel
                                                </Button>
                                                <Button type="submit" variant="outlined" color="success" size="small">
                                                    Save
                                                </Button>
                                            </Stack>
                                        }
                                    />
                                    <CardContent className="card-content" sx={{ maxHeight:'800px!important'}}>
                                        {/* SUMMARY */}
                                        <Grid container className="referral-section">
                                            <Typography variant="subtitle2">
                                                <u>SUMMARY</u>
                                            </Typography>
                                        </Grid>
                                        <Grid container className="referral-section">
                                            <Grid item xs={1}></Grid>
                                            <Grid item xs={9}>
                                                <TextField
                                                    margin="dense"
                                                    label="Issue Description"
                                                    multiline
                                                    rows={3}
                                                    name="issuesDescription"
                                                    fullWidth
                                                    defaultValue={selectedReferral.issuesDescription}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container className="referral-section">
                                            <Grid item xs={1}></Grid>
                                            <Grid item xs={9}>
                                                <TextField
                                                    margin="dense"
                                                    label="Relevant Background"
                                                    multiline
                                                    rows={3}
                                                    name="relevantBackground"
                                                    fullWidth
                                                    defaultValue={selectedReferral.relevantBackground}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container className="referral-section">
                                            <Grid item xs={1}></Grid>
                                            <Grid item xs={9}>
                                                <Grid item xs={9}>
                                                    <TextField
                                                        margin="dense"
                                                        label="Auditor Recommendation"
                                                        multiline
                                                        rows={3}
                                                        name="auditorRecommendation"
                                                        fullWidth
                                                        defaultValue={selectedReferral.auditorRecommendation}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid container className="referral-section">
                                            <Grid item xs={1}></Grid>
                                            <Grid item xs={9}>
                                                <TextField
                                                    margin="dense"
                                                    name="phoneCallDate"
                                                    label="Date Phone Call Made"
                                                    defaultValue={selectedReferral.callDate}
                                                    fullWidth
                                                />
                                            </Grid>
                                        </Grid>
                                        {/*ISSUES*/}
                                        <Grid container className="referral-section">
                                            <Typography variant="subtitle2">
                                                <u>ISSUES</u>
                                            </Typography>
                                        </Grid>
                                        {
                                            referralIssues?.map((issue) =>
                                                <Grid>
                                                    <Grid container className="referral-section">
                                                        <Grid item xs={1}></Grid>
                                                        <Grid item xs={10}>
                                                            <Stack direction="row" spacing={1}>
                                                                <Typography variant="subtitle2" sx={{ color: blue[800] }}>{issue.issueTypeCode} {issue.header}</Typography>
                                                                <DeleteOutline color="error" className="small-icon is-clickable" onClick={() => deleteIssueFromReferral(selectedReferral.id, issue.issueId)} />
                                                            </Stack>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container className="referral-section">
                                                        <Grid item xs={1}></Grid>
                                                        <Grid item xs={6}>
                                                            <TableContainer>
                                                                <Table stickyHeader sx={{ minWidth: 200 }} size="small" aria-label="Flagged Items Table">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell>Date</TableCell>
                                                                            <TableCell>Name</TableCell>
                                                                            <TableCell>Amount</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {
                                                                            issue.flaggedItems.map((flag) =>
                                                                                <TableRow key={flag.flaggedItemId}>
                                                                                    <TableCell>{flag.date}</TableCell>
                                                                                    <TableCell>{flag.name}</TableCell>
                                                                                    <TableCell align="right">{flag.amount}</TableCell>
                                                                                </TableRow>
                                                                            )
                                                                        }
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )
                                        }
                                        <input type="hidden" name="cpfId" value={selectedReferral.cpfId} />
                                        <input type="hidden" name="id" value={selectedReferral.id} />
                                    </CardContent>
                                </Card>
                        </form>
                    </Grid>
                }
            </Grid>
            {/*New Referral Dialog*/}
            <NewReferralDialog isNewReferralDialogOpen={isNewReferralDialogOpen} closeNewReferralDialog={closeNewReferralDialog}></NewReferralDialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog} confirmAction={confirmAction}></AlertInfoDialog>
            }
            {/*loading indicator*/}
            <CircularProgress
                size={50}
                className={isLoading}
                sx={{
                    position: "fixed",
                    left: "50%",
                    top: "30%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2
                }}
            />
        </div>
    )
}

export default ReferralManager;